import moment from 'moment';

const CAREOS_STATUS_BY_LOOM_STATUS = {
  ACTIVE: 'ACTIVE',
  REVIEW: 'REVIEW',
  DEACTIVATED: 'INACTIVE',
};

const LOOM_STATUSES_BY_CAREOS_STATUS = {
  ACTIVE: ['ACTIVE'],
  REVIEW: ['REVIEW'],
  INACTIVE: ['DEACTIVATED'],
};

const CAREOS_PRIORITY_BY_LOOM_LEVEL = {
  1: 'PRIORITY_1',
  2: 'PRIORITY_2',
  3: 'PRIORITY_3',
  4: 'PRIORITY_4',
};

const CAREOS_DAY_BY_LOOM_DAY = {
  SUNDAY: 'SU',
  MONDAY: 'MO',
  TUESDAY: 'TU',
  WEDNESDAY: 'WE',
  THURSDAY: 'TH',
  FRIDAY: 'FR',
  SATURDAY: 'SA',
};

// 2024-01-07 is a Sunday, so the offset lands each slot on its own weekday.
const WEEK_ANCHOR = '2024-01-07';
const ANCHOR_OFFSET_BY_LOOM_DAY = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const CERTIFICATION_FIELD_BY_LEVEL = {
  REQUIRED: 'certifications',
  AT_LEAST_ONE: 'atLeastOneCertifications',
  NICE_TO_HAVE: 'niceToHaveCertifications',
};

const LANGUAGE_FIELD_BY_LEVEL = {
  REQUIRED: 'languages',
  NICE_TO_HAVE: 'niceToHaveLanguages',
};

const personName = (person) => {
  return [person?.firstName, person?.lastName].filter(Boolean).join(' ');
};

const confirmationMap = (answers) => {
  if (!answers?.length) return undefined;

  return answers.reduce((result, answer) => ({...result, [answer.code]: answer.confirmed}), {});
};

// Loom keeps a weekday and two wall clocks; the career components measure a slot with
// end.diff(start) and spot an overnight one with isAfter(start, 'day'), so both need to be
// datetimes on the right weekday. The bump is the rule Schedule.jsx applies on save.
//
// Parsed as UTC, because the clocks are UTC: the ingest's wallClock() slices the hour digits
// out of the legacy ISO string, which moment wrote with toISOString(), so the timezone column
// never took part. Reading them as local printed every shift 4-5 hours late, and dev bears
// that out: 1111 of 1658 timezoned slots start between 10:00 and 18:00, which is the morning
// care peak displaced by the offset. The day stays Loom's, which is the intended local day.
const buildWeeklySlot = (slot) => {
  const offset = ANCHOR_OFFSET_BY_LOOM_DAY[slot.dayOfWeek] ?? 0;
  const start = moment.utc(`${WEEK_ANCHOR} ${slot.startTime}`, 'YYYY-MM-DD HH:mm:ss').add(offset, 'days');
  const end = moment.utc(`${WEEK_ANCHOR} ${slot.endTime}`, 'YYYY-MM-DD HH:mm:ss').add(offset, 'days');

  if (!end.isAfter(start)) {
    end.add(1, 'day');
  }

  return {
    day: CAREOS_DAY_BY_LOOM_DAY[slot.dayOfWeek],
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
};

// The ingest consumes schedule.type to build the slot rows and stores it nowhere, so the
// timezone is the only trace left: the create flow refuses to save a recurrence without
// one, and LIVEIN and FLEXIBLE are both hard-coded to null. Slot times cannot stand in for
// it, because a timed FLEXIBLE keeps real times too; of the 97 jobs in dev carrying no
// timezone, all 97 start every slot on a FLEXIBLE boundary and none is a recurrence.
const buildSchedule = (job) => {
  const slots = job?.scheduleSlots || [];

  if (!slots.some((slot) => slot.timezone)) return {};

  return {
    type: 'RECURRENCE_EVENT',
    recurrenceRule: {
      weeklySlots: slots.map(buildWeeklySlot),
      timezone: slots.find((slot) => slot.timezone)?.timezone,
    },
  };
};

// A CLIENT job has no location in Loom, and blank rather than undefined because
// translate() hands a substitution to String.replace, which would print "undefined".
const buildLocation = (job) => {
  const general = job?.generalJob;

  if (!general) return {neighborhood: '', city: ''};

  const hasGeoCode = general.locationLatitude != null && general.locationLongitude != null;

  return {
    neighborhood: general.locationNeighborhood || '',
    city: general.locationCity || '',
    geoCode: hasGeoCode ? {
      latitude: general.locationLatitude,
      longitude: general.locationLongitude,
    } : undefined,
  };
};

const buildClient = (job) => {
  const client = job?.clientJob?.client;

  if (client) {
    return {
      clientRn: client.legacyClientRn,
      clientName: personName(client.person),
    };
  }

  return {unregisteredClient: job?.generalJob?.postingOwnerName || undefined};
};

const buildRequirements = (job) => {
  const requirements = {
    careNeeds: (job?.careNeeds || []).map((entry) => entry.skill?.name).filter(Boolean),
    certifications: [],
    atLeastOneCertifications: [],
    niceToHaveCertifications: [],
    languages: [],
    niceToHaveLanguages: [],
    considerations: (job?.considerations || []).map((entry) => entry.consideration?.code).filter(Boolean),
    additionalConsiderations: {
      blob: job?.additionalConsiderations || '',
      editorType: 'MARKDOWN',
    },
  };

  for (const entry of job?.credentialRequirements || []) {
    const field = CERTIFICATION_FIELD_BY_LEVEL[entry.level];

    if (field && entry.credential?.name) requirements[field].push(entry.credential.name);
  }

  for (const entry of job?.languageRequirements || []) {
    const field = LANGUAGE_FIELD_BY_LEVEL[entry.level];

    if (field && entry.language?.name) requirements[field].push(entry.language.name);
  }

  return requirements;
};

const buildJob = (job) => {
  const schedule = buildSchedule(job);

  return {
    jobRn: job.legacyJobRn,
    status: CAREOS_STATUS_BY_LOOM_STATUS[job.status] || job.status,
    priority: CAREOS_PRIORITY_BY_LOOM_LEVEL[job.priorityLevel] || null,
    client: buildClient(job),
    location: buildLocation(job),
    description: {
      short: job.summary || '',
      long: {blob: job.description || '', editorType: 'MARKDOWN'},
    },
    // numeric(10,2) arrives as a string, and the card multiplies it out.
    price: {hourlyRate: Number(job.hourlyRate)},
    startDate: {startTime: job.startWindow},
    schedule,
    requirements: buildRequirements(job),
    priorities: job.priorities?.length ? [...job.priorities]
        .sort((first, second) => first.rank - second.rank)
        .map((entry) => entry.value) : undefined,
    applications: job.applications || [],
    createdAt: job.createdAt,
    updatedAt: job.updatedAt,
  };
};

// The card zips this against jobDetails.schedule.recurrenceRule.weeklySlots by index, so
// it is built off the job's slot order rather than the order the selections arrived in.
const buildSelectedShifts = (application, job, schedule) => {
  if (schedule.type !== 'RECURRENCE_EVENT') return {};

  const selectedSlotIds = new Set(
      (application.shiftSelections || []).map((selection) => selection.jobScheduleSlotId).filter(Boolean),
  );

  return {
    type: 'RECURRENCE_EVENT',
    recurrenceRule: {
      weeklySlots: (job.scheduleSlots || []).map((slot) => selectedSlotIds.has(slot.id)),
    },
  };
};

const buildApplication = (application, job, adaptedJob) => ({
  jobRn: job.legacyJobRn,
  caregiverRn: application.caregiver?.legacyCaregiverRn,
  caregiver: {name: personName(application.caregiver?.person)},
  source: application.source,
  status: application.status,
  answers: {
    canTravelToLocation: application.canTravelToLocation,
    additionalConsiderationConfirmations: application.additionalConsiderationsConfirmed,
    languages: confirmationMap(application.languageAnswers),
    certifications: confirmationMap(application.credentialAnswers),
    considerationConfirmations: confirmationMap(application.considerationAnswers),
    selectedShifts: buildSelectedShifts(application, job, adaptedJob.schedule),
  },
  jobDetails: {
    schedule: adaptedJob.schedule,
    requirements: adaptedJob.requirements,
  },
  createdAt: application.createdAt,
  updatedAt: application.updatedAt,
});

// No limit: the endpoint defaults to 100 to match the page size the legacy Job API
// hardcoded, so sending one here would page the sandbox differently from /career.
export const toLoomJobListQuery = (filters, sort, cursor) => {
  const query = {
    cursor,
    sortDirection: sort?.sortDirection?.toLowerCase() || 'desc',
  };
  const statuses = [];
  const kinds = [];

  for (const filter of filters || []) {
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];

    if (filter.key === 'status') {
      statuses.push(...values.flatMap((status) => LOOM_STATUSES_BY_CAREOS_STATUS[status] || []));
    }

    // No map for the kind: CLIENT and GENERAL are spelled the same on both sides, unlike the
    // statuses, where CareOS says INACTIVE and Loom says DEACTIVATED.
    if (filter.key === 'jobType') {
      kinds.push(...values.filter(Boolean));
    }
  }

  if (statuses.length) query.status = [...new Set(statuses)].join(',');
  if (kinds.length) query.kind = [...new Set(kinds)].join(',');

  return query;
};

export const adaptLoomJobList = (response) => ({
  list: (response?.jobs || []).map(buildJob),
  lastEvaluatedKey: response?.nextCursor || null,
});

export const adaptLoomJobDetail = (job) => {
  const adaptedJob = buildJob(job);
  const client = job.clientJob?.client;

  return {
    job: adaptedJob,
    applicants: (job.applications || []).map((application) => buildApplication(application, job, adaptedJob)),
    client: client ? {
      clientRn: client.legacyClientRn,
      clientDetail: {
        clientRn: client.legacyClientRn,
        clientName: personName(client.person),
      },
    } : null,
  };
};
